/**
 * 통합 관제 — 이 서버가 모아 보는 **다른 SignageOne 서버들**의 화면 현황과 원격 명령.
 *
 * 서버가 30초마다 그 서버들에서 요약을 받아 둔다(`/api/v1/hub/servers`). 이 화면은 그것을 그리고, 서버를
 * 고르면 그 서버의 화면 목록을 그 자리에서 받아 와 재부팅·앱 종료·전원 종료를 보낸다. 명령의 판단은 그
 * 서버가 하고, 거절하면 그 말을 그대로 보여 준다.
 *
 * **연결 끊김은 화면 장애가 아니다.** 그리드·망이 끊기면 그 서버의 화면을 알 수 없을 뿐이다 — 숫자를 지우지
 * 않고 마지막으로 받은 것을 흐리게, "연결 끊김" 과 함께 둔다. 합계에서도 오프라인으로 세지 않는다.
 *
 * 서버 등록·고치기·지우기는 환경 설정 권한(`settings:write` — 고객사 관리자·공급사)이 한다. 처음에는 공급사만
 * 했으나, 여러 서버를 둔 고객사가 스스로 묶게 관리자에게도 열었다(2026-10-26 사용자 지시).
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { LogOut, Pencil, Play, Plus, Power, RefreshCw, RotateCw, Trash2 } from 'lucide-react';
import type {
  AgentCommandKind,
  HubDisplay,
  HubServerList,
  HubServerView,
  HubState,
  SignageOneClient,
} from '../api/client.js';
import { COMMANDS, commandIsDanger } from '../components/AgentControlForm.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';
import { HelpTip } from '../components/HelpTip.js';
import { Badge, ErrorNote, ItemTitle, Loading, Modal, Panel, RelTime, StatusDot, relativeTime } from '../components/ui.js';
import { useUrlQuery } from '../components/urlState.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { liveStatus } from '../components/displayLive.js';

/* ───────────────────────────── 규칙(시험이 본다) ───────────────────────────── */

export const HUB_STATE_LABEL: Readonly<Record<HubState, string>> = {
  ok: '연결됨',
  unreachable: '연결 끊김',
  unauthorized: '토큰 거절',
  error: '응답 오류',
  pending: '확인 전',
};

const STATE_TONE: Readonly<Record<HubState, 'ok' | 'warn' | 'danger' | 'neutral'>> = {
  ok: 'ok',
  unreachable: 'danger',
  unauthorized: 'danger',
  error: 'warn',
  pending: 'neutral',
};

/** 연결됐어도 요약이 세 주기 넘게 낡았으면 "지연" — 수집기가 멈췄거나 그 서버가 느리다. */
export function hubStateLabel(server: Pick<HubServerView, 'state' | 'stale'>): { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' } {
  if (server.state === 'ok' && server.stale) return { label: '지연', tone: 'warn' };
  return { label: HUB_STATE_LABEL[server.state], tone: STATE_TONE[server.state] };
}

export interface HubTotals {
  servers: number;
  /** 지금 숫자를 믿을 수 있는 서버(연결됨·낡지 않음). */
  live: number;
  /** 닿지 않거나 거절됐거나 낡은 서버 — 그 서버의 화면은 지금 모른다. */
  unknown: number;
  total: number;
  online: number;
  offline: number;
  attention: number;
  maintenance: number;
}

/**
 * 맨 위 합계. **지금 믿을 수 있는 서버의 숫자만 더한다.** 끊긴 서버의 마지막 숫자를 더하면 끊기기 전의
 * 오프라인이 지금의 오프라인처럼 읽히고, 빼고 더하지 않으면 그 서버의 화면이 통째로 사라진 것처럼 보인다 —
 * 그래서 끊긴 서버 수를 따로 센다.
 */
export function hubTotals(servers: readonly HubServerView[]): HubTotals {
  const totals: HubTotals = { servers: servers.length, live: 0, unknown: 0, total: 0, online: 0, offline: 0, attention: 0, maintenance: 0 };
  for (const server of servers) {
    if (server.state !== 'ok' || server.stale || !server.summary) {
      totals.unknown += 1;
      continue;
    }
    totals.live += 1;
    totals.total += server.summary.total;
    totals.online += server.summary.online;
    totals.offline += server.summary.offline;
    totals.attention += server.summary.attention;
    totals.maintenance += server.summary.maintenance;
  }
  return totals;
}

/** 손볼 것이 많은 서버가 위로 — 끊긴 서버, 그다음 주의가 많은 서버, 그다음 이름. */
export function hubServerOrder(a: HubServerView, b: HubServerView): number {
  const rank = (server: HubServerView): number => (server.state === 'ok' && !server.stale ? 1 : 0);
  return (
    rank(a) - rank(b) ||
    (b.summary?.attention ?? 0) - (a.summary?.attention ?? 0) ||
    a.name.localeCompare(b.name, 'ko')
  );
}

/**
 * 그 서버의 화면에 이 명령을 걸 수 없는 까닭. 걸 수 있으면 null.
 *
 * 화면 상세(`commandBlock`)와 같은 판단이다 — 그 서버도 같은 것을 409 로 막는다. 여기서 먼저 막는 것은 눌러
 * 보고 나서야 알게 하지 않으려는 것이다.
 */
export function hubCommandBlock(display: HubDisplay, kind: AgentCommandKind, canCommand: boolean): string | null {
  const { label, since } = COMMANDS[kind];
  if (!canCommand) return '이 서버의 토큰은 조회 전용입니다 — 관리자(admin) 토큰으로 바꾸면 명령을 보낼 수 있습니다';
  if (!display.online) return `화면이 오프라인이라 ${label}할 수 없습니다`;
  if (kind === 'exit_maintenance' && !display.maintenance) return '정비 모드가 아닙니다';
  if (kind !== 'exit_maintenance' && display.maintenance) return `현장에서 정비 중입니다 — 정비 모드를 해제하거나 정비가 끝난 뒤에 ${label}하세요`;
  if (!display.commands || !display.commands.includes(kind)) return `원격 ${label}: 에이전트 ${since} 이상이 필요합니다`;
  if (kind === 'reboot' && !display.can_reboot) return '재부팅 권한이 없습니다(루트 또는 기기 소유자 필요). 현장에서 전원을 껐다 켜세요';
  if (kind === 'power_off' && !display.can_power_off) return '전원 종료 권한이 없습니다(루트 필요). 현장에서 끄세요';
  if (display.pending) return `${COMMANDS[display.pending].label} 명령을 보냈습니다. 화면이 받기를 기다립니다`;
  return null;
}

/** 화면 한 줄의 상태 말. */
export function hubDisplayState(display: HubDisplay): { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' } {
  if (!display.online) return { label: '오프라인', tone: 'danger' };
  if (display.maintenance) return { label: '정비 중', tone: 'warn' };
  if (display.last_error) return { label: '오류', tone: 'warn' };
  return { label: '온라인', tone: 'ok' };
}

/* ───────────────────────────── 화면 ───────────────────────────── */

const COMMAND_ICON = { reboot: RotateCw, quit: LogOut, power_off: Power, exit_maintenance: Play } as const;
const STATUS_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'attention', label: '주의' },
  { value: 'online', label: '온라인' },
  { value: 'offline', label: '오프라인' },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

export function HubScreen({ client, capabilities }: { client: SignageOneClient; capabilities: readonly string[] }): ReactNode {
  const servers = useAsync(() => client.listHubServers(), [client]);
  usePolling(servers.reload, 30_000);
  const action = useAction();
  const url = useUrlQuery();
  const selectedId = url.get('server');
  const canManage = capabilities.includes('settings:write');
  const [editing, setEditing] = useState<HubServerView | 'new' | null>(null);

  const list: HubServerList | null = servers.data;
  const items = [...(list?.items ?? [])].sort(hubServerOrder);
  const selected = items.find((server) => server.id === selectedId) ?? null;
  const totals = hubTotals(items);

  const replace = (next: HubServerView): void => {
    servers.reload();
    if (selectedId === next.id) url.set({ server: next.id });
  };

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {servers.error ? <ErrorNote message={servers.error} /> : null}
      {list && !list.module.enabled ? (
        <div className="settings-notice" role="status">
          통합 관제 모듈이 해제돼 있어 서버를 묻지 않습니다 ·{' '}
          {capabilities.includes('plugin:manage') ? '정비 모드 ▸ 플러그인에서 ‘통합 관제’를 사용으로 바꾸세요' : '공급사에 사용을 요청하세요'}
        </div>
      ) : null}

      <div className="stats">
        <div className="stat">
          <div className="stat-value">{totals.servers}</div>
          <div className="stat-label">
            서버{totals.unknown > 0 ? ` · 연결 끊김 ${totals.unknown}` : ''}
          </div>
        </div>
        <div className="stat">
          <div className="stat-value">{totals.total}</div>
          <div className="stat-label">화면{totals.unknown > 0 ? ' (끊긴 서버 제외)' : ''}</div>
        </div>
        <div className="stat stat-ok">
          <div className="stat-value">{totals.online}</div>
          <div className="stat-label">온라인</div>
        </div>
        <div className={`stat ${totals.attention > 0 ? 'stat-danger' : ''}`}>
          <div className="stat-value">{totals.attention}</div>
          <div className="stat-label">주의</div>
        </div>
        <div className={`stat ${totals.maintenance > 0 ? 'stat-warn' : ''}`}>
          <div className="stat-value">{totals.maintenance}</div>
          <div className="stat-label">정비 중</div>
        </div>
      </div>

      <Panel
        title={`서버 · ${items.length}대`}
        className="screen-fill"
        subtitle={list ? `${list.poll_sec}초마다 모읍니다${list.module.beta ? ' · 베타' : ''}` : undefined}
        help="등록한 서버마다 화면 요약을 30초마다 받아 옵니다. ‘연결 끊김’은 화면이 꺼진 것이 아니라 이 서버가 그 서버에 닿지 못하는 것입니다(그리드·망). 숫자는 마지막으로 받은 것을 흐리게 남깁니다."
        actions={
          canManage ? (
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> 서버 추가
            </button>
          ) : undefined
        }
      >
        {servers.loading && !list ? (
          <Loading />
        ) : items.length === 0 ? (
          <p className="hint p-4">
            {canManage
              ? '등록한 서버가 없습니다. [서버 추가]로 다른 서버의 주소와 서비스 토큰을 넣으세요.'
              : '등록한 서버가 없습니다. 관리자에게 서버 등록을 요청하세요.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>서버</th>
                  <th>연결</th>
                  <th>화면</th>
                  <th>주의</th>
                  <th>정비</th>
                  <th>마지막 수신</th>
                  <th aria-label="동작" />
                </tr>
              </thead>
              <tbody>
                {items.map((server) => (
                  <ServerRow
                    key={server.id}
                    server={server}
                    selected={server.id === selected?.id}
                    canManage={canManage}
                    pending={action.pending}
                    onOpen={() => url.set({ server: server.id === selected?.id ? null : server.id })}
                    onCheck={() => void action.run(() => client.checkHubServer(server.id), replace, (next) => `${next.name} — ${hubStateLabel(next).label}`)}
                    onEdit={() => setEditing(server)}
                    onDelete={async () => {
                      const ok = await confirmDialog({
                        title: `서버 지우기 — ${server.name}`,
                        body: '통합 관제에서 이 서버를 뺍니다. 그 서버와 화면에는 아무 일도 일어나지 않습니다.',
                        confirmLabel: '지우기',
                        danger: true,
                      });
                      if (!ok) return;
                      await action.run(
                        () => client.deleteHubServer(server.id),
                        () => {
                          if (selectedId === server.id) url.set({ server: null });
                          servers.reload();
                        },
                        `${server.name} 을(를) 뺐습니다`,
                      );
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selected ? <ServerDisplays key={selected.id} client={client} server={selected} /> : null}

      {editing ? (
        <ServerForm
          client={client}
          server={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            servers.reload();
            url.set({ server: saved.id });
          }}
        />
      ) : null}
    </div>
  );
}

function ServerRow({
  server,
  selected,
  canManage,
  pending,
  onOpen,
  onCheck,
  onEdit,
  onDelete,
}: {
  server: HubServerView;
  selected: boolean;
  canManage: boolean;
  pending: boolean;
  onOpen: () => void;
  onCheck: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): ReactNode {
  const state = hubStateLabel(server);
  const summary = server.summary;
  // 끊긴 서버의 숫자는 마지막으로 받은 것 — 흐리게 그려 지금 값이 아님을 드러낸다.
  const dim = server.state !== 'ok' || server.stale ? 'opacity-50' : '';
  return (
    <tr className={selected ? 'bg-slate-50' : undefined} style={{ cursor: 'pointer' }} onClick={onOpen} aria-selected={selected}>
      <td>
        {/* 줄을 누르면 그 서버의 화면을 고르고, 이름을 누르면 [고치기] 와 같다(다른 목록과 같다, 2026-10-22). */}
        <div>
          <ItemTitle onOpen={canManage && !pending ? onEdit : undefined} label={`${server.name} 고치기`}>
            {server.name}
          </ItemTitle>
        </div>
        <div className="cell-muted whitespace-nowrap font-mono text-[11.5px]">{server.url}</div>
      </td>
      <td>
        <Badge tone={state.tone}>{state.label}</Badge>
        {server.last_error ? (
          <div className="mt-1 max-w-[16rem] truncate text-[12px] text-slate-500" title={server.last_error}>
            {server.last_error}
          </div>
        ) : null}
        {server.remote && !server.remote.can_command ? <div className="mt-1 text-[12px] text-slate-500">조회 전용 토큰</div> : null}
      </td>
      <td className={`whitespace-nowrap ${dim}`}>{summary ? `${summary.online} / ${summary.total}` : '—'}</td>
      <td className={dim}>{summary ? <span className={summary.attention > 0 ? 'font-semibold text-rose-700' : ''}>{summary.attention}</span> : '—'}</td>
      <td className={dim}>{summary ? summary.maintenance : '—'}</td>
      <td className="cell-muted whitespace-nowrap">
        <RelTime iso={server.last_ok_at} />
      </td>
      <td onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <button type="button" className="btn btn-tiny" disabled={pending} onClick={onCheck} title="지금 묻기 — 토큰까지 다시 확인">
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> 지금 확인
          </button>
          {canManage ? (
            <>
              <IconButton icon={Pencil} label="고치기" subject={server.name} disabled={pending} onClick={onEdit} />
              <IconButton icon={Trash2} label="지우기" subject={server.name} danger disabled={pending} onClick={onDelete} />
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/** 고른 서버의 화면 목록과 명령 — 그 서버에서 그 자리에서 받아 온다(30초마다 다시). */
function ServerDisplays({ client, server }: { client: SignageOneClient; server: HubServerView }): ReactNode {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<HubDisplay[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [canCommand, setCanCommand] = useState(server.remote?.can_command === true);
  const action = useAction();

  // 검색어는 멈춘 뒤에 — 글자마다 그 서버를 부르지 않게.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const page = useAsync(async () => {
    const first = await client.hubDisplays(server.id, { status, q: search || undefined });
    setRows(first.items);
    setCursor(first.next_cursor);
    setCanCommand(first.server.can_command);
    return first;
  }, [client, server.id, status, search]);
  usePolling(page.reload, 30_000);

  const problems = (server.summary?.locations ?? []).filter((location) => location.offline > 0).slice(0, 8);

  const send = async (display: HubDisplay, kind: AgentCommandKind): Promise<void> => {
    const name = display.name || '(이름 없음)';
    const ok = await confirmDialog({
      title: `${COMMANDS[kind].label} — ${server.name} · ${name}`,
      body: COMMANDS[kind].confirm(name),
      confirmLabel: COMMANDS[kind].label,
      danger: commandIsDanger(kind),
    });
    if (!ok) return;
    await action.run(
      () => client.hubCommand(server.id, display.id, kind),
      () => page.reload(),
      `${server.name} · ${name} — ${COMMANDS[kind].label} 명령을 보냈습니다. 화면이 30초 안에 받습니다`,
    );
  };

  return (
    <Panel
      title={`${server.name} — 화면`}
      subtitle={server.url}
      help="그 서버에서 지금 받아 온 목록입니다. 명령은 그 서버가 판단해 화면에 걸고, 거절하면 그 서버의 말을 그대로 보여 줍니다."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input input-search w-full sm:w-auto"
            type="search"
            placeholder="이름·위치 검색"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="seg" role="group" aria-label="상태 필터">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className="seg-btn"
                aria-pressed={status === filter.value}
                onClick={() => setStatus(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {problems.length > 0 ? (
        <p className="border-b border-slate-100 px-4 py-2 text-[12.5px] text-slate-600">
          오프라인이 있는 위치:{' '}
          {problems.map((location) => (
            <span key={location.name || '(위치 없음)'} className="mr-2 inline-block">
              {location.name || '(위치 없음)'} <strong className="text-rose-700">{location.offline}</strong>/{location.total}
            </span>
          ))}
        </p>
      ) : null}
      {page.error ? (
        <p className="hint p-4">{page.error}</p>
      ) : page.loading && !page.data ? (
        <Loading />
      ) : rows.length === 0 ? (
        <p className="hint p-4">{search || status !== 'all' ? '조건에 맞는 화면이 없습니다.' : '이 서버에 등록된 화면이 없습니다.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>이름</th>
                <th>위치</th>
                <th>상태</th>
                <th>
                  버전
                  <HelpTip text="에이전트 버전. 주황이면 그 서버가 정한 목표 버전보다 뒤처졌습니다." label="버전 도움말" />
                </th>
                <th>마지막 보고</th>
                <th>원격 명령</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((display) => {
                const state = hubDisplayState(display);
                return (
                  <tr key={display.id} style={{ cursor: 'default' }}>
                    <td className="cell-strong">
                      <StatusDot online={display.online} /> {display.name || '(이름 없음)'}
                    </td>
                    <td className="cell-muted">{display.location || '—'}</td>
                    <td>
                      <Badge tone={state.tone}>{state.label}</Badge>
                      {/* 끊긴 화면은 "오프라인" 하나만 — 마지막 보고에 남은 오류는 지금 상태가 아니다(`liveStatus`). */}
                      {liveStatus(display.online, display.maintenance, display.last_error).error ? (
                        <div className="mt-1 max-w-[18rem] truncate text-[12px] text-slate-500" title={display.last_error ?? undefined}>
                          {display.last_error}
                        </div>
                      ) : null}
                    </td>
                    <td className={display.agent_behind ? 'font-medium text-amber-700' : 'cell-muted'} title={display.agent_wanted ? `목표 ${display.agent_wanted}` : undefined}>
                      {display.app_version || '—'}
                    </td>
                    <td className="cell-muted">{display.last_heartbeat_at ? relativeTime(display.last_heartbeat_at) : '—'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {/* 재부팅만 밖에, 되돌릴 수 없는 앱 종료·전원 종료는 [⋯] 뒤에(점진적 공개 — 화면 설정 창과 같은 규칙). */}
                        {(() => {
                          const block = hubCommandBlock(display, 'reboot', canCommand);
                          const Icon = COMMAND_ICON.reboot;
                          return (
                            <button
                              type="button"
                              className="btn btn-tiny btn-icon"
                              disabled={action.pending || block !== null}
                              title={block ?? COMMANDS.reboot.label}
                              aria-label={`${display.name} ${COMMANDS.reboot.label}`}
                              onClick={() => void send(display, 'reboot')}
                            >
                              <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                            </button>
                          );
                        })()}
                        <MoreMenu
                          label={`${display.name} 원격 명령 더 보기`}
                          items={[
                            // 정비 중인 화면에만 — 현장에서 열어 둔 채 떠난 정비 화면을 닫는다.
                            ...(liveStatus(display.online, display.maintenance, null).maintenance
                              ? (['exit_maintenance'] as const)
                              : []),
                            ...(['quit', 'power_off'] as const),
                          ].map((kind) => {
                            const block = hubCommandBlock(display, kind, canCommand);
                            return {
                              label: COMMANDS[kind].label,
                              danger: commandIsDanger(kind),
                              disabled: action.pending || block !== null,
                              title: block ?? undefined,
                              onSelect: () => void send(display, kind),
                            };
                          })}
                        />
                        {display.pending ? <span className="ml-1 text-[12px] text-slate-500">{COMMANDS[display.pending].label} 대기</span> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {cursor ? (
        <div className="border-t border-slate-100 p-3 text-center">
          <button
            type="button"
            className="btn"
            disabled={action.pending}
            onClick={() =>
              void action.run(
                () => client.hubDisplays(server.id, { status, q: search || undefined, cursor }),
                (next) => {
                  setRows((before) => [...before, ...next.items]);
                  setCursor(next.next_cursor);
                },
              )
            }
          >
            더 보기
          </button>
        </div>
      ) : null}
    </Panel>
  );
}

/** 서버 추가·고치기. 서버가 적기 전에 그 서버에 닿는지·토큰이 통하는지 보고, 아니면 이유를 돌려준다. */
function ServerForm({
  client,
  server,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  server: HubServerView | null;
  onClose: () => void;
  onSaved: (saved: HubServerView) => void;
}): ReactNode {
  const [name, setName] = useState(server?.name ?? '');
  const [address, setAddress] = useState(server?.url ?? '');
  const [token, setToken] = useState('');
  const action = useAction();

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void action.run(
      () =>
        server
          ? client.updateHubServer(server.id, {
              ...(name.trim() !== server.name ? { name: name.trim() } : {}),
              ...(address.trim() !== server.url ? { url: address.trim() } : {}),
              ...(token.trim() ? { token: token.trim() } : {}),
            })
          : client.createHubServer({ name: name.trim(), url: address.trim(), token: token.trim() }),
      onSaved,
      (saved) => `${saved.name} — ${hubStateLabel(saved).label}`,
    );
  };

  return (
    <Modal title={server ? `서버 고치기 — ${server.name}` : '서버 추가'} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="field">
          <span className="field-label">이름</span>
          <input
            className="input"
            required
            autoFocus
            maxLength={80}
            placeholder="판교 사옥"
            value={name}
            disabled={action.pending}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">
            주소
            <HelpTip
              text="그 서버의 접속 주소입니다. 망이 다른 현장은 Sentinel Grid 주소(10.64.0.0/10 대역 — 그 서버의 signageonectl grid status 나 sentinel 콘솔 ▸ 그리드)에 접속 포트를 붙입니다. 같은 사내망이면 사내 주소를 그대로 씁니다."
              label="주소 도움말"
            />
          </span>
          <input
            className="input font-mono"
            required
            placeholder="http://10.64.0.12:24600"
            value={address}
            disabled={action.pending}
            onChange={(event) => setAddress(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">
            서비스 토큰
            <HelpTip
              text="그 서버의 콘솔 ▸ 환경 설정 ▸ 통합 관제 연결에서 만든 토큰(sk_…)입니다. 관리자(admin) 토큰이면 원격 명령까지, 조회(viewer) 토큰이면 보기만 합니다."
              label="토큰 도움말"
            />
          </span>
          <input
            className="input font-mono"
            required={!server}
            autoComplete="off"
            placeholder={server ? `그대로 두려면 비워 두세요 (지금 …${server.token_hint})` : 'sk_…'}
            value={token}
            disabled={action.pending}
            onChange={(event) => setToken(event.target.value)}
          />
        </label>
        <p className="hint">저장하기 전에 그 서버에 닿는지와 토큰이 통하는지 확인합니다.</p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '확인 중…' : '저장'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
